import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import https from '../../utils/https';

// --- Types ---
interface Role {
    key: string;
    name: string;
    [key: string]: any;
}

interface User {
    _id: string;
    first_name: string;
    last_name: string;
    username: string;
    email: string;
    phone_number: string;
    role: Role[];
    createdAt: string;
    updatedAt: string;
    status: string;
    is_active: boolean;
    [key: string]: any;
}

interface AuthState {
    user: User | null;
    token: string | null;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: string | null;
}

// --- Async Thunks ---

// Login Thunk
export const loginUser = createAsyncThunk(
    'auth/loginUser',
    async (credentials: { mobile?: string; otp?: string; username?: string; password?: string, roleId: string }, { rejectWithValue }) => {
        try {
            let response;

            if (!credentials.roleId) {
                return rejectWithValue('Role is required for login');
            }

            if (credentials.username && credentials.password) {
                // Username/Password Login: /users/login/{role_id}
                response = await https.post(`/users/login/${credentials.roleId}`, {
                    email: credentials.username,
                    password: credentials.password
                });
            } else if (credentials.mobile && credentials.otp) {
                // Mobile/OTP Login: /users/verify-otp/{role_id}
                response = await https.post(`/users/verify-otp/${credentials.roleId}`, {
                    phone_number: credentials.mobile,
                    otp: credentials.otp
                });
            } else {
                return rejectWithValue('Invalid credentials provided');
            }

            // Handle API Response
            if (response && response.success) {
                return response.data;
            } else {
                return rejectWithValue(response?.message || 'Login failed');
            }

        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || error.message || 'Login failed');
        }
    }
);

// Register Thunk
export const registerUser = createAsyncThunk(
    'auth/registerUser',
    async (userData: {
        first_name: string;
        last_name?: string;
        email: string;
        password?: string;
        phone_number?: string;
        otp?: string;
        roleId?: any
    }, { rejectWithValue }) => {
        try {
            // const { rest } = userData;
            const response = await https.post(`/users`, userData);

            if (response && response.success) {
                return response.data;
            } else {
                return rejectWithValue(response?.message || 'Registration failed');
            }
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || error.message || 'Registration failed');
        }
    }
);

// Update User Profile Thunk
export const updateUserProfile = createAsyncThunk(
    'auth/updateUserProfile',
    async (profileData: { first_name: string; last_name?: string; email?: string }, { rejectWithValue }) => {
        try {
            const response = await https.put(`/users/profile`, profileData);

            if (response && response.success) {
                return response.data;
            } else {
                return rejectWithValue(response?.message || 'Failed to update profile');
            }
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || error.message || 'Failed to update profile');
        }
    }
);

// Send OTP Thunk
export const sendOtp = createAsyncThunk(
    'auth/sendOtp',
    async (data: { mobile: string, roleId: string }, { rejectWithValue }) => {
        try {
            const response = await https.post(`/users/send-otp/${data.roleId}`, {
                phone_number: data.mobile
            });

            if (response && response.success) {
                return response.data;
            } else {
                return rejectWithValue(response?.message || 'Failed to send OTP');
            }
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || error.message || 'Failed to send OTP');
        }
    }
);

// --- Slice ---

const initialState: AuthState = {
    user: typeof window !== 'undefined' && localStorage.getItem('user')
        ? JSON.parse(localStorage.getItem('user') || 'null')
        : null,
    token: typeof window !== 'undefined' ? localStorage.getItem('token') : null,
    status: 'idle',
    error: null,
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        logout: (state) => {
            state.user = null;
            state.token = null;
            state.status = 'idle';
            state.error = null;
            if (typeof window !== 'undefined') {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
            }
        },
        clearError: (state) => {
            state.error = null;
        },
        setDummyUser: (state, action: PayloadAction<{ user: any; token: string }>) => {
            state.user = action.payload.user;
            state.token = action.payload.token;
            state.status = 'succeeded';
            state.error = null;
            if (typeof window !== 'undefined') {
                localStorage.setItem('token', action.payload.token);
                localStorage.setItem('user', JSON.stringify(action.payload.user));
            }
        },
    },
    extraReducers: (builder) => {
        // Login
        builder
            .addCase(loginUser.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(loginUser.fulfilled, (state, action: PayloadAction<any>) => {
                state.status = 'succeeded';
                const { access_token, ...rest } = action.payload;

                if (rest.status === 'inactive') {
                    state.token = access_token;
                    state.user = null;
                    if (typeof window !== 'undefined') {
                        if (access_token) localStorage.setItem('token', access_token);
                        localStorage.removeItem('user');
                    }
                } else {
                    state.token = access_token;
                    state.user = rest as User;
                    if (typeof window !== 'undefined') {
                        if (access_token) localStorage.setItem('token', access_token);
                        if (rest) localStorage.setItem('user', JSON.stringify(rest));
                    }
                }
            })
            .addCase(loginUser.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
            });

        // Register
        builder
            .addCase(registerUser.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(registerUser.fulfilled, (state, action: PayloadAction<any>) => {
                state.status = 'succeeded';
                const { access_token, ...rest } = action.payload;
                const token = access_token || action.payload.token || state.token || (typeof window !== 'undefined' ? localStorage.getItem('token') : null);
                const user = rest.user || rest;

                state.token = token as string | null;
                state.user = user as User;
                if (typeof window !== 'undefined') {
                    if (token) localStorage.setItem('token', token);
                    localStorage.setItem('user', JSON.stringify(user));
                }
            })
            .addCase(registerUser.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
            });

        // Update User Profile
        builder
            .addCase(updateUserProfile.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(updateUserProfile.fulfilled, (state, action: PayloadAction<any>) => {
                state.status = 'succeeded';
                state.user = action.payload as User;
                if (typeof window !== 'undefined') {
                    localStorage.setItem('user', JSON.stringify(action.payload));
                }
            })
            .addCase(updateUserProfile.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
            });
    },
});

export const { logout, clearError, setDummyUser } = authSlice.actions;
export default authSlice.reducer;
